import { Fragment, useRef, useState } from 'react'
import {
  COVERAGE_LABEL,
  type Person,
  type PersonId,
  type Policy,
  type PolicySource,
} from '@asvise/household-engine/vocabulary'
import type { LoadedDocument } from '@asvise/read'
import { isEmpty, toPolicy } from '@asvise/household-extract/policy'
import {
  GATE_LABEL,
  HEADER_FIELDS,
  HEADER_LABEL,
  type BenefitReading,
  type DocumentReading,
  type Gate,
  type GateFinding,
  type HeaderField,
  type ReadOutcome,
} from '@asvise/household-extract/types'
import { planDocument, readDocument } from '../lib/extract/run'
import { suggestMember } from '../lib/names'

/**
 * Reading a life or health policy out of the PDF the insurer sent.
 *
 * The commercial app's import panel, rebuilt around what a life schedule
 * actually grants. There a document gives sections of cover with limits and
 * sub-limits; here it gives benefits to a named person, several of which can be
 * three claims against one sum. So the review list is benefit-shaped, every
 * pool total is a row of its own, and two questions are asked that have no
 * commercial counterpart.
 *
 * The first is who each benefit insures. A schedule prints a name, and matching
 * "Daniel Tan; male; non-smoker at issue" to a member of this household is a
 * judgement only the household can make. `toPolicy` therefore leaves
 * `insured_person_id` null and the engine excludes unmatched cover from every
 * total, which is correct and also means an upload nobody attributes changes
 * nothing. Asking here is what turns a read document into cover that counts.
 *
 * The second is whether the policy came through work. No schedule says so, and
 * framework section 6.2 treats employer cover as ending with the job unless
 * evidence says otherwise. A default would quietly make that evidence
 * unnecessary, so it is a question.
 *
 * Everything else is the commercial panel's discipline, kept because it is what
 * makes an extraction safe to show: every value appears with the words it was
 * read from and the page they are on, skipping is exactly as easy as keeping,
 * what was discarded is listed with the reason, and the reconstructed page text
 * is there so a model that invented a row can be told apart from a page that
 * was rebuilt badly.
 *
 * Corrections are deliberately not made here, for the commercial panel's
 * reason: a value edited in the record is stamped as the household's own and
 * outranks the document, and having two places that can do that gives you a
 * second one that drifts.
 */

/**
 * CHECKED sits between opening the file and reading it.
 *
 * Framework section 5.2 puts its seven checks before extraction, and a stage
 * is what makes "before" mean anything: a pack that arrived missing nine of
 * its twelve pages is shown to somebody while it is still free to choose
 * another file. A document that passes never stops here.
 */
type Stage = 'IDLE' | 'CHECKING' | 'CHECKED' | 'BUSY' | 'REVIEW'

const money = (n: number): string => `S$${n.toLocaleString('en-SG')}`

/** Who a benefit insures, where nobody has said yet. */
const UNMATCHED = ''

/**
 * What a benefit row amounts to, said exactly.
 *
 * A reimbursement benefit has an annual limit and no sum assured, and calling
 * that limit a sum is the error that reads a hospital plan as income
 * replacement. They are different fields in the engine and they are different
 * sentences here.
 */
function figureOf(benefit: BenefitReading): { value: string; note?: string } {
  if (!benefit.operative) {
    return {
      value: 'Not insured',
      note: 'The schedule lists this benefit and shows it as not included. That is the document answering the question, which is a different fact from a benefit it never mentions.',
    }
  }

  if (benefit.amount !== null) return { value: money(benefit.amount.value) }

  if (benefit.annual_limit !== null) {
    return {
      value: `${money(benefit.annual_limit.value)} a year`,
      note: 'An annual reimbursement ceiling, which is not a sum assured. It pays a hospital against bills; it does not replace income.',
    }
  }

  return {
    value: 'No figure stated',
    note: 'The benefit was found but no amount was. It is recorded as unread rather than as nil, so the screening asks about it instead of reporting it as settled.',
  }
}

/** What a header reading says, in the words a reader checks against the quote. */
function headerValue(reading: DocumentReading, field: HeaderField): string {
  const held = reading[field]
  if (held === null) return ''
  if (field === 'premium_amount') return money(held.value as number)
  if (field === 'premium_guaranteed') {
    return held.value === true
      ? 'Guaranteed for the term'
      : 'Not guaranteed, so what this costs can change'
  }
  return String(held.value)
}

/**
 * The sub-readings under one benefit, each a row somebody can refuse.
 *
 * The pool is not among them, although it is read per benefit. A schedule
 * states its shared sum once and the reading repeats it against every benefit
 * that draws on one, so three identical rows quoting one line appeared here,
 * and refusing any one of them did nothing: the conversion pools every
 * poolable benefit as soon as any of them names a total. A row that looks
 * refusable and is not is worse than no row, so the pool is hoisted to a
 * single row for the policy, where refusing it means what it appears to mean.
 */
function detailsOf(benefit: BenefitReading): {
  field: keyof BenefitReading
  label: string
  value: string
  note?: string
}[] {
  const out: { field: keyof BenefitReading; label: string; value: string; note?: string }[] = []

  if (benefit.basis !== null) {
    out.push({
      field: 'basis',
      label: 'How it pays',
      value:
        benefit.basis.value === 'ACCELERATED'
          ? 'Paid out of the sum the other benefits share'
          : 'A sum of its own, on top of the others',
    })
  }

  if (benefit.waiting_days !== null) {
    out.push({
      field: 'waiting_days',
      label: 'Waiting period',
      value: `${benefit.waiting_days.value} days from the start before it can be claimed`,
    })
  }

  if (benefit.survival_days !== null) {
    out.push({
      field: 'survival_days',
      label: 'Survival period',
      value: `${benefit.survival_days.value} days must be survived for it to pay`,
    })
  }

  if (benefit.expires_at_age !== null) {
    out.push({
      field: 'expires_at_age',
      label: 'Ends at age',
      value: String(benefit.expires_at_age.value),
      note: 'A benefit that has already ended for this person is not counted as cover, which a schedule will never correct you on.',
    })
  }

  if (benefit.expires_on !== null) {
    out.push({ field: 'expires_on', label: 'Ends on', value: benefit.expires_on.value })
  }

  if (benefit.deductible !== null) {
    out.push({
      field: 'deductible',
      label: 'Deductible',
      value: `${money(benefit.deductible.value)} of each year's bills is yours`,
    })
  }

  if (benefit.co_insurance !== null) {
    out.push({
      field: 'co_insurance',
      label: 'Co-insurance',
      value: `${Math.round(benefit.co_insurance.value * 100)}% of what is left is yours`,
    })
  }

  if (benefit.annual_limit !== null && benefit.amount !== null) {
    out.push({
      field: 'annual_limit',
      label: 'Annual limit',
      value: `${money(benefit.annual_limit.value)} a year`,
    })
  }

  if (benefit.out_of_pocket_cap !== null) {
    out.push({
      field: 'out_of_pocket_cap',
      label: 'Most you would pay',
      value: `${money(benefit.out_of_pocket_cap.value)} in a year`,
    })
  }

  if (benefit.cost_sharing_rider !== null) {
    out.push({
      field: 'cost_sharing_rider',
      label: 'Rider on the cost sharing',
      value: benefit.cost_sharing_rider.value
        ? 'A rider reduces what you pay'
        : 'No rider reduces what you pay',
    })
  }

  if (benefit.insured_as_printed !== null) {
    out.push({
      field: 'insured_as_printed',
      label: 'Printed as insuring',
      value: benefit.insured_as_printed.value,
    })
  }

  return out
}

const VERDICT_WORD: Record<GateFinding['verdict'], string> = {
  PASS: 'Yes',
  REVIEW: 'Needs a look',
  UNRESOLVED: 'Not established',
}

/**
 * The seven checks, and what the document showed against each.
 *
 * Printed in full whether or not anything failed, which is the same choice the
 * result screen makes about evidence: a list that appears only when something
 * is wrong tells a reader nothing about the times it stayed away. An
 * unresolved check is shown as its own thing rather than as a pass, because
 * the difference is exactly what somebody reading this is entitled to assume.
 */
function GatePanel({ gate }: { gate: Gate }) {
  return (
    <div className={`import-gate ${gate.verdict === 'READY' ? 'ready' : 'marked'}`}>
      <h4>
        {gate.verdict === 'READY'
          ? 'This document can be read'
          : 'This document can be read, with something to check first'}
      </h4>
      {gate.verdict !== 'READY' && (
        <p className="import-note">
          Nothing here stops you. What it changes is what we will claim: cover from a document we
          could not check is reported and is counted towards no shortfall, because a benefit read
          correctly off the pages that arrived is not the same thing as cover being in force at
          that size.
        </p>
      )}
      <ul className="import-gate-rows">
        {gate.findings.map((finding) => (
          <li key={finding.check} className={`gate-${finding.verdict.toLowerCase()}`}>
            <div className="import-head">
              <span className="import-label">{GATE_LABEL[finding.check]}</span>
              <span className="import-value">{VERDICT_WORD[finding.verdict]}</span>
            </div>
            <p className="import-row-note">{finding.said}</p>
            {finding.citation && (
              <Quote page={finding.citation.page} verbatim={finding.citation.verbatim} />
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** What a citation actually claims: these words, on that page. */
function Quote({ page, verbatim }: { page: number | null; verbatim: string }) {
  return (
    <p className="import-quote">
      {page !== null && <span className="import-page">page {page}</span>}
      <span className="import-verbatim">{verbatim}</span>
    </p>
  )
}

function Row({
  label,
  value,
  note,
  page,
  verbatim,
  skipped,
  indented = false,
  onToggle,
}: {
  label: string
  value: string
  note?: string
  page: number | null
  verbatim: string
  skipped: boolean
  /** A detail qualifies the benefit above it, rather than standing beside it. */
  indented?: boolean
  onToggle: () => void
}) {
  return (
    <li
      className={['import-row', indented && 'under', skipped && 'skipped']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="import-head">
        <span className="import-label">{label}</span>
        <span className="import-value">{value}</span>
        <button type="button" className="import-toggle" onClick={onToggle}>
          {skipped ? 'Keep' : 'Skip'}
        </button>
      </div>
      <Quote page={page} verbatim={verbatim} />
      {note && <p className="import-row-note">{note}</p>}
    </li>
  )
}

export function PolicyImport({
  people,
  onAdd,
}: {
  people: Person[]
  onAdd: (policy: Policy) => void
}) {
  const [stage, setStage] = useState<Stage>('IDLE')
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<ReadOutcome | null>(null)
  const [skipped, setSkipped] = useState<Set<string>>(new Set())
  const [dragging, setDragging] = useState(false)
  /**
   * Who each printed name is, as the household says.
   *
   * Keyed by the name the schedule prints rather than by the benefit, because
   * one answer settles every benefit insuring that person and asking five times
   * about one name is how a review screen gets skipped.
   */
  const [gate, setGate] = useState<Gate | null>(null)
  /**
   * The opened file, held between the gate and the reading.
   *
   * A ref rather than state: nothing renders from it, and putting a twelve
   * page document in state would re-render the review list on every change for
   * no reason at all.
   */
  const held = useRef<LoadedDocument | null>(null)
  const [insuredBy, setInsuredBy] = useState<Record<string, PersonId | ''>>({})
  /**
   * What the household chose for the owner, or undefined where it has not.
   *
   * Three states and not two, because a suggestion is shown until somebody
   * decides: "nobody" is a choice and has to beat it, which a plain empty
   * string could not say.
   */
  const [ownerChoice, setOwnerChoice] = useState<PersonId | '' | undefined>(undefined)
  const [source, setSource] = useState<PolicySource>('PERSONAL')
  const [portable, setPortable] = useState<'YES' | 'NO' | 'UNSURE'>('UNSURE')
  /**
   * Shown while reading, because a request queued behind somebody else's looks
   * exactly like a model thinking, and the elapsed seconds are the only thing
   * on screen that tells the two apart.
   */
  const [waited, setWaited] = useState(0)
  const running = useRef<AbortController | null>(null)

  const toggle = (key: string) =>
    setSkipped((held) => {
      const next = new Set(held)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const reset = () => {
    setStage('IDLE')
    setGate(null)
    held.current = null
    setOutcome(null)
    setSkipped(new Set())
    setInsuredBy({})
    setOwnerChoice(undefined)
    setSource('PERSONAL')
    setPortable('UNSURE')
    setError(null)
  }

  /**
   * Open the file, ask the seven checks, and read it if it has nothing to
   * answer for.
   *
   * The two halves are split here rather than inside the reader because only
   * this half is free. Opening a PDF and running the gate costs one request;
   * reading it costs a model call per benefit, and spending those on a pack
   * that arrived in pieces is the waste the gate exists to prevent.
   */
  async function run(file: File) {
    setError(null)
    setGate(null)
    setOutcome(null)
    setSkipped(new Set())
    setInsuredBy({})
    setStage('CHECKING')

    const controller = new AbortController()
    running.current = controller

    try {
      /**
       * Fetched only once there is a file to open. A PDF reader and its worker
       * are most of a megabyte, and a household that never uploads anything
       * should not be made to download them.
       */
      const { loadPdf } = await import('@asvise/pdf')
      const doc = await loadPdf(file)
      if (doc.scanned) {
        throw new Error(
          'This PDF holds scanned images rather than text, so there is nothing here to read or to quote. A policy can still be entered by hand.',
        )
      }

      const checked = await planDocument(doc, controller.signal)
      held.current = doc
      setGate(checked.gate)

      if (checked.gate.verdict !== 'READY') {
        setStage('CHECKED')
        return
      }

      await read(doc, controller)
    } catch (thrown) {
      if (controller.signal.aborted) {
        setStage('IDLE')
        return
      }
      setError(thrown instanceof Error ? thrown.message : 'The file could not be read.')
      setStage('IDLE')
    } finally {
      running.current = null
    }
  }

  async function read(doc: LoadedDocument, controller: AbortController) {
    setStage('BUSY')
    setWaited(0)
    const ticking = window.setInterval(() => setWaited((n) => n + 1), 1000)
    try {
      setOutcome(await readDocument(doc, controller.signal))
      setStage('REVIEW')
    } finally {
      window.clearInterval(ticking)
    }
  }

  /** Read a document the gate marked, which is a choice rather than a default. */
  async function readAnyway() {
    const doc = held.current
    if (!doc) return
    const controller = new AbortController()
    running.current = controller
    try {
      await read(doc, controller)
    } catch (thrown) {
      if (controller.signal.aborted) {
        setStage('IDLE')
        return
      }
      setError(thrown instanceof Error ? thrown.message : 'The file could not be read.')
      setStage('CHECKED')
    } finally {
      running.current = null
    }
  }

  /** A benefit's row key, which is where it sits rather than what it covers. */
  const keyOf = (benefit: BenefitReading, index: number) => `benefit:${benefit.code}:${index}`

  /**
   * The benefits that would become cover, in the order `toPolicy` will take
   * them.
   *
   * It filters on `operative` and keeps the order, so this list and
   * `policy.benefits` line up one to one and the person chosen for a row can be
   * applied by position. The ids are taken before the filter, so refusing one
   * row does not renumber the rows below it.
   */
  const granted = outcome
    ? outcome.reading.benefits
        .map((benefit, index) => ({ benefit, id: keyOf(benefit, index) }))
        .filter(({ benefit, id }) => benefit.operative && !skipped.has(id))
    : []

  /** Which printed name a benefit's cover should be attributed under. */
  const nameFor = ({ benefit, id }: { benefit: BenefitReading; id: string }): string =>
    skipped.has(`${id}:insured_as_printed`)
      ? UNMATCHED
      : (benefit.insured_as_printed?.value ?? UNMATCHED)

  /**
   * The member a printed name is, where it is exactly one.
   *
   * A suggestion and never a decision: it is shown selected, the household can
   * change it or say "not sure", and an explicit choice always wins. The reason
   * for being narrow about it is in `lib/names.ts`.
   */
  const suggestedFor = (printed: string): PersonId | '' =>
    printed === UNMATCHED ? '' : ((suggestMember(printed, people) as PersonId | null) ?? '')

  /** What a printed name is currently matched to: the household's choice, else the suggestion. */
  const insuredFor = (printed: string): PersonId | '' =>
    insuredBy[printed] ?? suggestedFor(printed)

  const ownerPrinted = outcome?.reading.policyholder?.value ?? UNMATCHED
  const ownerNow: PersonId | '' = ownerChoice ?? suggestedFor(ownerPrinted)

  /** One review row per printed name, naming the benefits it would decide. */
  const groups = (() => {
    const held = new Map<string, { printed: string; codes: string[] }>()
    for (const row of granted) {
      const name = nameFor(row)
      const found = held.get(name)
      if (found) found.codes.push(COVERAGE_LABEL[row.benefit.code])
      else held.set(name, { printed: name, codes: [COVERAGE_LABEL[row.benefit.code]] })
    }
    return [...held.entries()].map(([key, value]) => ({ key, ...value }))
  })()

  /**
   * The shared sum, stated once for the policy.
   *
   * Taken from the first benefit that names one, because a schedule states one
   * pool and the reading carries it against each benefit that draws on it.
   */
  const pool = outcome?.reading.benefits.find((b) => b.operative && b.pool_total !== null)
    ?.pool_total

  /** What survived review, which is what becomes a policy. */
  function kept(reading: DocumentReading): DocumentReading {
    const header = Object.fromEntries(
      HEADER_FIELDS.map((f) => [f, skipped.has(f) ? null : reading[f]]),
    ) as Pick<DocumentReading, HeaderField>

    return {
      ...reading,
      ...header,
      benefits: reading.benefits
        .map((benefit, index) => ({ benefit, id: keyOf(benefit, index) }))
        .filter(({ id }) => !skipped.has(id))
        .map(({ benefit, id }) => {
          const next = { ...benefit }
          /**
           * Refused for the policy rather than for a benefit, because that is
           * what the schedule said and what the conversion reads. Dropping it
           * everywhere is the only refusal that changes anything: one benefit
           * naming a total pools every benefit that could draw on it.
           */
          if (skipped.has('pool')) next.pool_total = null
          for (const detail of detailsOf(benefit)) {
            if (skipped.has(`${id}:${detail.field}`)) {
              /**
               * Dropped to null, which is where it sat before anybody read
               * anything. Nothing is asserted by refusing a row: an unread
               * waiting period is unknown rather than none, and the engine
               * treats those two differently on purpose.
               */
              next[detail.field] = null as never
            }
          }
          return next
        }),
    }
  }

  const surviving = outcome ? kept(outcome.reading) : null

  /** Everything the household was asked, applied to what the document bore out. */
  function assembled(reading: DocumentReading): Policy {
    /**
     * The gate's findings travel with the policy, because this is the last
     * moment anybody holds both. A marked policy cannot produce a figure the
     * comparison treats as the cover available, which is framework section
     * 5.2's consequence and the only part of the gate that outlives this
     * screen.
     */
    const policy = toPolicy(reading, new Date().toISOString(), gate?.review_reasons ?? [])
    return {
      ...policy,
      /**
       * Confirmed here, because this is the screen where somebody confirmed it.
       *
       * Every value above was shown with the line it was read from and could
       * have been refused one at a time. Pressing the button is the human
       * review framework section 6.3 asks for, and the only place that fact
       * exists is this click.
       */
      confirmed_at: new Date().toISOString(),
      policyholder_person_id: ownerNow === UNMATCHED ? null : ownerNow,
      source,
      /**
       * Only asked about employer cover, because it is only employer cover the
       * question means anything for. The engine reads `portable` when the
       * source is EMPLOYER and ignores it otherwise, so a personal policy keeps
       * whatever `toPolicy` recorded rather than gaining an answer nobody gave.
       */
      portable:
        source === 'EMPLOYER'
          ? portable === 'YES'
            ? true
            : portable === 'NO'
              ? false
              : policy.portable
          : policy.portable,
      benefits: policy.benefits.map((benefit, index) => {
        const row = granted[index]
        const chosen = row ? insuredFor(nameFor(row)) : undefined
        return {
          ...benefit,
          insured_person_id: chosen === undefined || chosen === UNMATCHED ? null : chosen,
        }
      }),
    }
  }

  const unmatched = groups.filter((g) => insuredFor(g.key) === UNMATCHED)

  return (
    <div className="import">
      {stage === 'IDLE' && (
        <>
          <label
            className={['import-drop', dragging && 'dragging']
              .filter(Boolean)
              .join(' ')}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              const file = e.dataTransfer.files?.[0]
              if (file) void run(file)
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) void run(file)
              }}
            />
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <strong>Read a policy from its PDF</strong>
            <small>
              Click to upload or drag and drop. The schedule, the benefit wording and any
              endorsements usually arrive as one file, so add it whole.
            </small>
          </label>

          {error && <p className="import-error">{error}</p>}

          {/*
            Worth saying, because the opposite is what people assume of an
            upload: the file never leaves the device. Only the text of the pages
            that read as a schedule is sent, and only those.
          */}
          <p className="import-note">
            The file is opened in this browser. Only the text of the pages that read as a policy
            schedule is sent to be read, and the file itself is not stored anywhere. We never ask a
            document for anything about anybody's health.
          </p>
        </>
      )}

      {stage === 'CHECKING' && (
        <p className="import-busy">Opening the file and checking it over…</p>
      )}

      {stage === 'CHECKED' && gate && (
        <div className="import-review">
          <GatePanel gate={gate} />
          <div className="import-actions">
            <button type="button" className="primary" onClick={() => void readAnyway()}>
              Read it anyway
            </button>
            <button type="button" className="link" onClick={reset}>
              Choose another file
            </button>
            <span className="import-unmatched">
              If you have the whole pack, or the other policy on its own, that is the better file.
            </span>
          </div>
        </div>
      )}

      {stage === 'BUSY' && (
        <p className="import-busy">
          Reading the document… {waited}s
          <button
            type="button"
            className="link"
            onClick={() => {
              running.current?.abort()
              reset()
            }}
          >
            Stop
          </button>
        </p>
      )}

      {stage === 'REVIEW' && outcome && surviving && (
        <div className="import-review">
          {gate && <GatePanel gate={gate} />}

          <header className="import-review-head">
            <h4>What the document says</h4>
            <p>
              {outcome.summary}. Everything below will be added. Skip anything the quoted words do
              not support, and enter it by hand instead.
            </p>
          </header>

          <ul className="import-rows">
            {HEADER_FIELDS.map((field) => {
              if (outcome.reading[field] === null) return null
              const held = outcome.reading[field]
              if (held === null) return null
              return (
                <Row
                  key={field}
                  label={HEADER_LABEL[field]}
                  value={headerValue(outcome.reading, field)}
                  page={held.citation.page}
                  verbatim={held.citation.verbatim}
                  skipped={skipped.has(field)}
                  onToggle={() => toggle(field)}
                />
              )
            })}

            {pool !== undefined && pool !== null && (
              <Row
                label="Shared sum"
                value={`Death, disability and critical illness share ${money(pool.value)}`}
                note="Benefits drawing on one sum cannot all be paid. None of the amounts below is added to another, and paying one reduces what is left for the rest. Refusing this row treats them as separate sums, which is what overstates a family's protection."
                page={pool.citation.page}
                verbatim={pool.citation.verbatim}
                skipped={skipped.has('pool')}
                onToggle={() => toggle('pool')}
              />
            )}

            {outcome.reading.benefits.map((benefit, index) => {
              const id = keyOf(benefit, index)
              return (
                <Fragment key={id}>
                  <Row
                    label={COVERAGE_LABEL[benefit.code]}
                    {...figureOf(benefit)}
                    page={benefit.found.page}
                    verbatim={benefit.found.verbatim}
                    skipped={skipped.has(id)}
                    onToggle={() => toggle(id)}
                  />

                  {detailsOf(benefit).map((detail) => {
                    const held = benefit[detail.field]
                    if (held === null || typeof held !== 'object' || !('citation' in held)) {
                      return null
                    }
                    return (
                      <Row
                        key={`${id}:${detail.field}`}
                        indented
                        label={detail.label}
                        value={detail.value}
                        note={detail.note}
                        page={held.citation.page}
                        verbatim={held.citation.verbatim}
                        skipped={skipped.has(`${id}:${detail.field}`)}
                        onToggle={() => toggle(`${id}:${detail.field}`)}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </ul>

          {outcome.reading.benefits.length === 0 && (
            <p className="import-note">
              No benefit was read from this schedule. The policy can still be added by hand.
            </p>
          )}

          {/*
            The two questions no schedule answers, asked where the answers are
            about to matter. Framework section 6.3 calls for a confirmation
            screen before assessment; these are the parts of it that are not
            simply confirming a quoted line.
          */}
          <div className="import-attribute">
            <h4>Who this cover belongs to</h4>
            <p className="import-note">
              A schedule prints a name. Where it is exactly the name of one member of your family,
              that member is already chosen and you can change it. Otherwise only you can say who it
              is. Cover nobody has matched is kept on the record and counted towards nothing.
            </p>

            {groups.map((group) => (
              <fieldset key={group.key} className="import-match">
                <legend>
                  {group.printed === UNMATCHED
                    ? 'Benefits with no name printed against them'
                    : `The schedule says: ${group.printed}`}
                </legend>
                <p className="import-note">{group.codes.join(', ')}</p>
                <label>
                  <span className="sub-label">Who is this?</span>
                  <select
                    value={insuredFor(group.key)}
                    onChange={(e) =>
                      setInsuredBy((held) => ({
                        ...held,
                        [group.key]: e.target.value as PersonId | '',
                      }))
                    }
                  >
                    <option value={UNMATCHED}>Nobody in this family, or not sure</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.label}
                      </option>
                    ))}
                  </select>
                </label>
                {insuredBy[group.key] === undefined && suggestedFor(group.printed) !== '' && (
                  <p className="import-note">
                    Chosen because the name is the same as {people.find((p) => p.id === insuredFor(group.key))?.label}. Check
                    it, and change it if this is somebody else.
                  </p>
                )}
              </fieldset>
            ))}

            {outcome.reading.policyholder !== null && (
              <fieldset className="import-match">
                <legend>The contract is owned by: {outcome.reading.policyholder.value}</legend>
                <label>
                  <span className="sub-label">Who is this?</span>
                  <select
                    value={ownerNow}
                    onChange={(e) => setOwnerChoice(e.target.value as PersonId | '')}
                  >
                    <option value={UNMATCHED}>Nobody in this family, or not sure</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.label}
                      </option>
                    ))}
                  </select>
                </label>
                {ownerChoice === undefined && suggestedFor(ownerPrinted) !== '' && (
                  <p className="import-note">
                    Chosen because the name is the same as {people.find((p) => p.id === ownerNow)?.label}. This records who holds the
                    contract and changes no figure.
                  </p>
                )}
              </fieldset>
            )}

            <fieldset className="import-match">
              <legend>Did this policy come through work?</legend>
              <p className="import-note">
                No schedule says, and it changes what the cover is worth to you: a benefit that
                ends with the job ends at the moment the family's income does.
              </p>
              <label>
                <input
                  type="radio"
                  name="policy-source"
                  checked={source === 'PERSONAL'}
                  onChange={() => setSource('PERSONAL')}
                />
                No, we bought it ourselves
              </label>
              <label>
                <input
                  type="radio"
                  name="policy-source"
                  checked={source === 'EMPLOYER'}
                  onChange={() => setSource('EMPLOYER')}
                />
                Yes, it is an employer benefit
              </label>
            </fieldset>

            {source === 'EMPLOYER' && (
              <fieldset className="import-match">
                <legend>Would it continue after a job change?</legend>
                <p className="import-note">
                  Treated as ending with the job unless the documents say otherwise, so "not sure"
                  is read the same way as "no" until something proves it.
                </p>
                {(
                  [
                    ['YES', 'Yes, the documents say it continues'],
                    ['NO', 'No, it ends with the job'],
                    ['UNSURE', 'Not sure'],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value}>
                    <input
                      type="radio"
                      name="policy-portable"
                      checked={portable === value}
                      onChange={() => setPortable(value)}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>
            )}
          </div>

          {outcome.discarded.length > 0 && (
            <div className="import-dropped">
              <p className="import-note">
                {outcome.discarded.length}{' '}
                {outcome.discarded.length === 1 ? 'reading was' : 'readings were'} left out. Each
                one is shown with the words that were quoted for it, so a model that paraphrased
                can be told apart from a schedule that genuinely does not say it.
              </p>
              <ul>
                {outcome.discarded.map((d, i) => (
                  <li key={i}>
                    <span className="import-label">{d.what}</span>
                    <span className="import-why">{d.why}</span>
                    <span className="import-verbatim">{d.quoted}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="import-note">
            Exclusions and definitions live in the policy wording, which this reading did not
            cover, so an empty list of exclusions is not a claim that this policy excludes nothing.
            Correct anything afterwards in the record itself, which stores it as yours rather than
            as the document's.
          </p>

          {/* The text every quote was matched against. A dropped reading is a
              claim about words nobody can see, and it has two opposite causes:
              a model that invented a row, or a page that was rebuilt wrongly.
              This is the only thing that tells them apart. */}
          <details className="import-read">
            <summary>What was read from this file</summary>
            {outcome.read.map((page) => (
              <div key={page.page}>
                <p className="import-page">page {page.page}</p>
                <pre>{page.text}</pre>
              </div>
            ))}
          </details>

          <div className="import-actions">
            <button
              type="button"
              className="primary"
              disabled={isEmpty(surviving)}
              onClick={() => {
                onAdd(assembled(surviving))
                reset()
              }}
            >
              Add this policy
            </button>
            <button type="button" className="link" onClick={reset}>
              Discard
            </button>
            {unmatched.length > 0 && (
              <span className="import-unmatched">
                {unmatched.length === groups.length
                  ? 'No cover here is matched to anybody yet, so none of it will count towards a shortfall.'
                  : `${unmatched.length} of ${groups.length} printed names are unmatched. That cover will not count towards a shortfall.`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
